/**
 * Video Grid Section JavaScript
 * 
 * Features:
 * - Auto-play videos when scrolled into view (Intersection Observer)
 * - Pause videos when out of view
 * - Handle mobile touch events
 * - Lazy load videos
 */

class VideoGrid {
  constructor(sectionId) {
    this.section = document.getElementById(sectionId);
    if (!this.section) return;

    this.videos = this.section.querySelectorAll('.video-card__video');
    this.observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Video must be 50% visible
    };

    this.init();
  }

  init() {
    // Set up Intersection Observer for auto-play
    this.setupIntersectionObserver();

    // Handle video errors
    this.handleVideoErrors();

    // Add click to play/pause functionality
    this.setupClickHandlers();

    // Mobile scroll optimization
    this.setupMobileScroll();
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        const shouldAutoplay = video.dataset.autoplayOnScroll === 'true';

        if (entry.isIntersecting && shouldAutoplay) {
          // Video is in view - play it
          this.playVideo(video);
        } else {
          // Video is out of view - pause it
          this.pauseVideo(video);
        }
      });
    }, this.observerOptions);

    // Observe all videos
    this.videos.forEach(video => {
      observer.observe(video);
    });
  }

  playVideo(video) {
    if (video.paused) {
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started playing
            video.setAttribute('data-playing', 'true');
          })
          .catch(error => {
            // Auto-play was prevented
            console.warn('Video autoplay prevented:', error);
            video.setAttribute('data-playing', 'false');
          });
      }
    }
  }

  pauseVideo(video) {
    if (!video.paused) {
      video.pause();
      video.setAttribute('data-playing', 'false');
    }
  }

  handleVideoErrors() {
    this.videos.forEach(video => {
      video.addEventListener('error', (e) => {
        console.error('Video loading error:', e);
        const card = video.closest('.video-card');
        if (card) {
          card.classList.add('video-card--error');
        }
      });

      video.addEventListener('loadstart', () => {
        video.setAttribute('data-loading', 'true');
      });

      video.addEventListener('loadeddata', () => {
        video.removeAttribute('data-loading');
      });
    });
  }

  setupClickHandlers() {
    this.videos.forEach(video => {
      // Disable right-click context menu
      video.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        return false;
      });

      // Prevent click events from pausing the video
      video.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });

      // Disable keyboard controls
      video.addEventListener('keydown', (e) => {
        e.preventDefault();
        return false;
      });
    });
  }

  setupMobileScroll() {
    const grid = this.section.querySelector('.video-grid--scrollable');
    if (!grid) return;

    let isScrolling;
    
    grid.addEventListener('scroll', () => {
      // Clear timeout if it exists
      window.clearTimeout(isScrolling);

      // Pause all videos while scrolling
      this.videos.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
      });

      // Set a timeout to run after scrolling ends
      isScrolling = setTimeout(() => {
        // Find the video most in view and play it
        this.playVisibleVideo();
      }, 150);
    }, { passive: true });
  }

  playVisibleVideo() {
    let mostVisibleVideo = null;
    let maxVisibility = 0;

    this.videos.forEach(video => {
      const rect = video.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Calculate how much of the video is visible
      const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
      const visibility = visibleWidth / rect.width;

      if (visibility > maxVisibility && visibility > 0.5) {
        maxVisibility = visibility;
        mostVisibleVideo = video;
      }
    });

    // Play the most visible video if autoplay is enabled
    if (mostVisibleVideo && mostVisibleVideo.dataset.autoplayOnScroll === 'true') {
      this.playVideo(mostVisibleVideo);
    }
  }
}

// Initialize all video grid sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const videoSections = document.querySelectorAll('[id^="video-grid-"]');
  
  videoSections.forEach(section => {
    new VideoGrid(section.id);
  });
});

// Handle Shopify theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[id^="video-grid-"]');
    if (section) {
      new VideoGrid(section.id);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Clean up if needed
    const videos = event.target.querySelectorAll('.video-card__video');
    videos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
  });
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VideoGrid;
}
